/**
 * Client Edit (SystemAdmin, or a user granted CanManageClients).
 *
 * Editable: Name, Description, IsActive, billing address (Street/City/State/ZIP/
 * Country/Phone), TimeZone, NmvtisRetentionDays.
 *
 * NOT editable here: security policies (RequireMfa, PasswordExpirationDays,
 * EnableApiKeyAccess) — those are ClientAdmin self-service on
 * PATCH /api/clients/{id}/policies and shouldn't be touched from this admin
 * page. The current values are surfaced read-only for visibility.
 *
 * Some actions (deleting the organization, API keys, invoicing/client-management
 * grants, role changes) remain SystemAdmin-only on the backend and are hidden here
 * for a CanManageClients-only user.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, Loader2, ArrowLeft, Save, CheckCircle, AlertCircle, Trash2, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import ClientUsersKeysSection from '../../components/admin/ClientUsersKeysSection';
import ClientStatePricingSection from '../../components/admin/ClientStatePricingSection';
import ClientServicePricingSection from '../../components/admin/ClientServicePricingSection';
import ClientPricingTiersSection from '../../components/admin/ClientPricingTiersSection';
import NetSuiteCustomerPicker from '../../components/admin/NetSuiteCustomerPicker';
import InvoiceRecipientsList from '../../components/admin/InvoiceRecipientsList';
import { getErrorMessage } from '../../services/apiService';
import {
  getClientAdmin,
  updateClientAdmin,
  deleteClientAdmin,
  IANA_TIMEZONES,
  ClientAdminResponse,
  UpdateClientAdminRequest,
  InvoiceFrequency,
  BankAccountType,
} from '../../services/clientsAdminService';
import { maskPhoneInput } from '../../utils/validation';
import ClientDocuments from '../../components/ClientDocuments';

const Section: React.FC<{ title: string; defaultOpen?: boolean; allowOverflow?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, allowOverflow = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`bg-white rounded-2xl border border-black/[0.08] shadow-sm ${allowOverflow ? 'overflow-visible' : 'overflow-hidden'}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-6 py-4 text-left bg-black/[0.02] hover:bg-black/[0.04] transition-colors ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569]">{title}</h2>
        <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-6 py-5">{children}</div>}
    </div>
  );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">{children}</label>
);

const TextInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
  />
);

const NumberInput: React.FC<{ value: number; onChange: (v: number) => void; min?: number }> = ({ value, onChange, min = 1 }) => (
  <input
    type="number"
    min={min}
    value={value}
    onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || min))}
    className="w-32 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
  />
);

// Retention: empty means "use the global default". Only a SystemAdmin can set a number.
const RetentionInput: React.FC<{ value: number | null; onChange: (v: number | null) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <input
    type="number"
    min={1}
    value={value ?? ''}
    placeholder="Global default"
    disabled={disabled}
    onChange={(e) => {
      const n = parseInt(e.target.value, 10);
      onChange(Number.isNaN(n) ? null : Math.max(1, n));
    }}
    className="w-40 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-[#f1f5f9] disabled:text-[#64748b]"
  />
);

const TextArea: React.FC<{ value: string; onChange: (v: string) => void; rows?: number }> = ({ value, onChange, rows = 3 }) => (
  <textarea
    value={value}
    rows={rows}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 resize-y"
  />
);

const Toggle: React.FC<{ label: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, value, onChange }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#c41e3a]" />
    <span className="text-sm text-[#0f172a]">{label}</span>
  </label>
);

const ReadOnly: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <div className="text-sm text-[#0f172a]">{value ?? <span className="text-[#94a3b8]">—</span>}</div>
  </div>
);

interface FormState {
  name: string;
  description: string;
  isActive: boolean;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  timeZone: string;
  nmvtisRetentionDays: number | null;
  // NetSuite linkage
  netSuiteCustomerId: string; // empty string = not linked / cleared
  // Onboarding contact + billing
  contactName: string;
  contactEmail: string;
  autoPay: boolean;
  invoiceFrequency: InvoiceFrequency;
  autoInvoicingEnabled: boolean;
  invoiceTermsId: string; // empty = use system default
  bankName: string;
  bankAccountHolderName: string;
  bankRoutingNumber: string;
  bankAccountNumber: string; // new value only; empty = leave the stored number unchanged
  bankAccountType: BankAccountType | null;
  // Security policies
  requireMfa: boolean;
  passwordExpirationDays: number; // 0 = never expires
  enableApiKeyAccess: boolean;
  // Compliance
  mvrDppaPermissibleUse: string; // empty string = not set (null on server)
  nmvtisCustomerCategory: string; // empty string = not set (null on server)
}

const toFormState = (c: ClientAdminResponse): FormState => ({
  name: c.name,
  description: c.description ?? '',
  isActive: c.isActive,
  streetAddress: c.streetAddress ?? '',
  city: c.city ?? '',
  state: c.state ?? '',
  zipCode: c.zipCode ?? '',
  country: c.country ?? '',
  phone: c.phone ?? '',
  timeZone: c.timeZone,
  nmvtisRetentionDays: c.nmvtisRetentionDays,
  netSuiteCustomerId: c.netSuiteCustomerId ?? '',
  contactName: c.contactName ?? '',
  contactEmail: c.contactEmail ?? '',
  autoPay: c.autoPay,
  invoiceFrequency: c.invoiceFrequency,
  autoInvoicingEnabled: c.autoInvoicingEnabled ?? false,
  invoiceTermsId: c.invoiceTermsId ?? '',
  bankName: c.bankName ?? '',
  bankAccountHolderName: c.bankAccountHolderName ?? '',
  bankRoutingNumber: c.bankRoutingNumber ?? '',
  bankAccountNumber: '',
  bankAccountType: c.bankAccountType,
  requireMfa: c.requireMfa,
  passwordExpirationDays: c.passwordExpirationDays ?? 0,
  enableApiKeyAccess: c.enableApiKeyAccess,
  mvrDppaPermissibleUse: c.mvrDppaPermissibleUse ?? '',
  nmvtisCustomerCategory: c.nmvtisCustomerCategory ?? '',
});

const diff = (orig: FormState, cur: FormState): UpdateClientAdminRequest => {
  const out: UpdateClientAdminRequest = {};
  if (cur.name !== orig.name) out.name = cur.name;
  if (cur.description !== orig.description) out.description = cur.description;
  if (cur.isActive !== orig.isActive) out.isActive = cur.isActive;
  if (cur.streetAddress !== orig.streetAddress) out.streetAddress = cur.streetAddress || null;
  if (cur.city !== orig.city) out.city = cur.city || null;
  if (cur.state !== orig.state) out.state = cur.state || null;
  if (cur.zipCode !== orig.zipCode) out.zipCode = cur.zipCode || null;
  if (cur.country !== orig.country) out.country = cur.country || null;
  if (cur.phone !== orig.phone) out.phone = cur.phone || null;
  if (cur.timeZone !== orig.timeZone) out.timeZone = cur.timeZone;
  if (cur.nmvtisRetentionDays !== orig.nmvtisRetentionDays) out.nmvtisRetentionDays = cur.nmvtisRetentionDays ?? 0;
  // Backend: null = no-op, "" = clear, "id" = set. Pass the value as-is.
  if (cur.netSuiteCustomerId !== orig.netSuiteCustomerId) {
    out.netSuiteCustomerId = cur.netSuiteCustomerId;
  }
  if (cur.contactName !== orig.contactName) out.contactName = cur.contactName || null;
  if (cur.contactEmail !== orig.contactEmail) out.contactEmail = cur.contactEmail || null;
  if (cur.autoPay !== orig.autoPay) out.autoPay = cur.autoPay;
  if (cur.invoiceFrequency !== orig.invoiceFrequency) out.invoiceFrequency = cur.invoiceFrequency;
  if (cur.autoInvoicingEnabled !== orig.autoInvoicingEnabled) out.autoInvoicingEnabled = cur.autoInvoicingEnabled;
  if (cur.invoiceTermsId !== orig.invoiceTermsId) out.invoiceTermsId = cur.invoiceTermsId || null;
  if (cur.bankName !== orig.bankName) out.bankName = cur.bankName || null;
  if (cur.bankAccountHolderName !== orig.bankAccountHolderName) out.bankAccountHolderName = cur.bankAccountHolderName || null;
  if (cur.bankRoutingNumber !== orig.bankRoutingNumber) out.bankRoutingNumber = cur.bankRoutingNumber || null;
  if (cur.bankAccountType !== orig.bankAccountType) out.bankAccountType = cur.bankAccountType;
  if (cur.bankAccountNumber) out.bankAccountNumber = cur.bankAccountNumber; // only when a new value is typed
  if (cur.requireMfa !== orig.requireMfa) out.requireMfa = cur.requireMfa;
  if (cur.passwordExpirationDays !== orig.passwordExpirationDays) out.passwordExpirationDays = cur.passwordExpirationDays;
  if (cur.enableApiKeyAccess !== orig.enableApiKeyAccess) out.enableApiKeyAccess = cur.enableApiKeyAccess;
  if (cur.mvrDppaPermissibleUse !== orig.mvrDppaPermissibleUse) out.mvrDppaPermissibleUse = cur.mvrDppaPermissibleUse || null;
  if (cur.nmvtisCustomerCategory !== orig.nmvtisCustomerCategory) out.nmvtisCustomerCategory = cur.nmvtisCustomerCategory || null;
  return out;
};

const ClientEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;
  const canManageClients = isSystemAdmin || user?.canManageClients === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!canManageClients) navigate('/dashboard');
  }, [user, authLoading, navigate, canManageClients]);

  const [data, setData] = useState<ClientAdminResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkingSaving, setLinkingSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!data) return;
    setDeleting(true);
    setError('');
    try {
      await deleteClientAdmin(data.id);
      navigate('/admin/clients');
    } catch (err) {
      setError(getErrorMessage(err));
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError('');
      const c = await getClientAdmin(id);
      setData(c);
      setForm(toFormState(c));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { if (canManageClients) load(); }, [canManageClients, load]);

  const handleNetSuiteLink = async (nsId: string | null) => {
    if (!id) return;
    setForm((f) => f ? { ...f, netSuiteCustomerId: nsId ?? '' } : f);
    setLinkingSaving(true);
    setError('');
    try {
      // Backend pattern: null = "no-op", "" = clear. Use "" when unlinking.
      const updated = await updateClientAdmin(id, { netSuiteCustomerId: nsId ?? '' });
      setData(updated);
      setForm(toFormState(updated));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLinkingSaving(false);
    }
  };

  const handleSave = async () => {
    if (!id || !data || !form) return;
    if (!form.name.trim()) { setError('Name cannot be blank.'); return; }
    if (!form.timeZone) { setError('TimeZone is required.'); return; }

    const orig = toFormState(data);
    const patch = diff(orig, form);
    if (Object.keys(patch).length === 0) return;

    try {
      setSaving(true);
      setError('');
      const updated = await updateClientAdmin(id, patch);
      setData(updated);
      setForm(toFormState(updated));
      setSuccess('Changes saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || isLoading || !data || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  const orig = toFormState(data);
  const dirty = Object.keys(diff(orig, form)).length > 0;
  const setF = (k: keyof FormState, v: FormState[keyof FormState]) => setForm({ ...form, [k]: v } as FormState);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <button onClick={() => navigate('/admin/clients')} className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#c41e3a] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Clients
          </button>

          <div className="flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[#c41e3a]" />
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a]">{data.name}</h1>
              <p className="text-sm text-[#64748b]">Created {new Date(data.createdAt).toLocaleDateString()} · Last updated {new Date(data.updatedAt).toLocaleDateString()}</p>
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-xl border flex items-start space-x-3 bg-red-50 border-red-200">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-4 rounded-xl border flex items-start space-x-3 bg-emerald-50 border-emerald-200">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">{success}</p>
            </div>
          )}

          <Section title="Identity" allowOverflow>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Name</Label>
                <TextInput value={form.name} onChange={(v) => setF('name', v)} />
              </div>
              <div className="flex items-end">
                <Toggle label="Active" value={form.isActive} onChange={(v) => setF('isActive', v)} />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <TextArea value={form.description} onChange={(v) => setF('description', v)} />
              </div>
              <div className="md:col-span-2">
                <Label>NetSuite Customer</Label>
                <NetSuiteCustomerPicker
                  linkedId={form.netSuiteCustomerId || undefined}
                  companyName={form.name}
                  contactEmail={form.contactEmail}
                  phone={form.phone}
                  disabled={linkingSaving}
                  onLink={handleNetSuiteLink}
                  onPrefill={(fields) =>
                    setForm((f) => {
                      if (!f) return f;
                      return {
                        ...f,
                        // Only backfill fields that are currently empty/blank
                        name: f.name.trim() ? f.name : (fields.companyName ?? f.name),
                        contactEmail: f.contactEmail.trim() ? f.contactEmail : (fields.contactEmail ?? f.contactEmail),
                        phone: f.phone.trim() ? f.phone : (fields.phone ?? f.phone),
                        streetAddress: f.streetAddress.trim() ? f.streetAddress : (fields.streetAddress ?? f.streetAddress),
                        city: f.city.trim() ? f.city : (fields.city ?? f.city),
                        state: f.state.trim() ? f.state : (fields.state ?? f.state),
                        zipCode: f.zipCode.trim() ? f.zipCode : (fields.zipCode ?? f.zipCode),
                      };
                    })
                  }
                />
              </div>
            </div>
          </Section>

          <Section title="Billing &amp; Banking">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Contact name</Label>
                <TextInput value={form.contactName} onChange={(v) => setF('contactName', v)} />
              </div>
              <div>
                <Label>Contact email</Label>
                <TextInput value={form.contactEmail} onChange={(v) => setF('contactEmail', v)} />
              </div>
              <div className="md:col-span-2">
                <Label>Street Address</Label>
                <TextInput value={form.streetAddress} onChange={(v) => setF('streetAddress', v)} />
              </div>
              <div>
                <Label>City</Label>
                <TextInput value={form.city} onChange={(v) => setF('city', v)} />
              </div>
              <div>
                <Label>State</Label>
                <TextInput value={form.state} onChange={(v) => setF('state', v)} placeholder="2-char code preferred" />
              </div>
              <div>
                <Label>ZIP</Label>
                <TextInput value={form.zipCode} onChange={(v) => setF('zipCode', v)} />
              </div>
              <div>
                <Label>Country</Label>
                <TextInput value={form.country} onChange={(v) => setF('country', v)} />
              </div>
              <div>
                <Label>Phone</Label>
                <TextInput value={form.phone} placeholder="(555) 555-0100" onChange={(v) => setF('phone', maskPhoneInput(v))} />
              </div>
              <div className="flex items-end">
                <Toggle label="AutoPay" value={form.autoPay} onChange={(v) => setF('autoPay', v)} />
              </div>
              <div>
                <Label>Invoice frequency</Label>
                <select
                  value={form.invoiceFrequency}
                  onChange={(e) => setF('invoiceFrequency', Number(e.target.value) as InvoiceFrequency)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  <option value={InvoiceFrequency.Monthly}>Monthly</option>
                  <option value={InvoiceFrequency.BiWeekly}>Bi-weekly</option>
                </select>
              </div>
              <div className="flex items-end">
                <Toggle label="Auto-Invoicing" value={form.autoInvoicingEnabled} onChange={(v) => setF('autoInvoicingEnabled', v)} />
              </div>
              <div>
                <Label>Invoice Terms</Label>
                <select
                  value={form.invoiceTermsId}
                  onChange={(e) => setF('invoiceTermsId', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  <option value="">System default (Due on receipt)</option>
                  <option value="7">Net 10</option>
                  <option value="1">Net 15</option>
                  <option value="2">Net 30</option>
                  <option value="3">Net 60</option>
                  <option value="4">Due on receipt</option>
                </select>
              </div>
              <div>
                <Label>Bank name</Label>
                <TextInput value={form.bankName} onChange={(v) => setF('bankName', v)} />
              </div>
              <div>
                <Label>Account holder</Label>
                <TextInput value={form.bankAccountHolderName} onChange={(v) => setF('bankAccountHolderName', v)} />
              </div>
              <div>
                <Label>Routing number</Label>
                <TextInput value={form.bankRoutingNumber} onChange={(v) => setF('bankRoutingNumber', v)} />
              </div>
              <div>
                <Label>Account type</Label>
                <select
                  value={form.bankAccountType ?? ''}
                  onChange={(e) => setF('bankAccountType', e.target.value === '' ? null : (Number(e.target.value) as BankAccountType))}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  <option value="">—</option>
                  <option value={BankAccountType.Checking}>Checking</option>
                  <option value={BankAccountType.Savings}>Savings</option>
                </select>
              </div>
              <div>
                <Label>Account number</Label>
                <TextInput
                  value={form.bankAccountNumber}
                  onChange={(v) => setF('bankAccountNumber', v)}
                  placeholder={data.bankAccountNumberLast4 ? `•••• ${data.bankAccountNumberLast4} (leave blank to keep)` : 'Enter account number'}
                />
                <p className="text-xs text-[#94a3b8] mt-1">Stored encrypted. Only the last 4 are ever shown; leave blank to keep the current number.</p>
              </div>
            </div>
          </Section>

          <Section title="Data Retention">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Timezone</Label>
                <select
                  value={form.timeZone}
                  onChange={(e) => setF('timeZone', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  {IANA_TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <p className="text-xs text-[#64748b] mt-2">
                  Anchors midnight for this client's retention clock — state DMV, NMVTIS, and NVS purges all use this TZ.
                </p>
              </div>
              <div>
                <Label>NMVTIS Retention (days)</Label>
                <RetentionInput value={form.nmvtisRetentionDays} onChange={(v) => setF('nmvtisRetentionDays', v)} disabled={!isSystemAdmin} />
                <p className="text-xs text-[#64748b] mt-2 leading-relaxed">
                  Calendar days a saved NMVTIS or NVS report is kept before the nightly purge
                  deletes it. Empty means the global default applies. State DMV records use the
                  per-state retention configured on the Jurisdictions page (anchored to this
                  client's TZ). {isSystemAdmin
                    ? 'Only a SystemAdmin can change this.'
                    : 'Only a SystemAdmin can change this; ask one if this client needs a longer period.'}
                </p>
              </div>
              <ReadOnly label="NMVTIS Max Retention (reserved)" value={data.nmvtisRetentionMaxDays ?? 'Not enforced'} />
            </div>
          </Section>

          {/* SystemAdmin may set these on behalf of the org; ClientAdmins also
              manage their own via PATCH /api/clients/{id}/policies. */}
          <Section title="Security Policies">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <Toggle label="MFA Required" value={form.requireMfa} onChange={(v) => setF('requireMfa', v)} />
              <div>
                <label className="block text-xs font-semibold text-[#64748b] mb-1">Password Expiration (days)</label>
                <NumberInput value={form.passwordExpirationDays} onChange={(v) => setF('passwordExpirationDays', v)} min={0} />
                <p className="text-[11px] text-[#94a3b8] mt-1">0 = never expires</p>
              </div>
              <Toggle label="API Key Access" value={form.enableApiKeyAccess} onChange={(v) => setF('enableApiKeyAccess', v)} />
            </div>
          </Section>

          <Section title="Compliance">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>MVR DPPA Permissible Use</Label>
                <select
                  value={form.mvrDppaPermissibleUse}
                  onChange={(e) => setF('mvrDppaPermissibleUse', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  <option value="">— Not set (MVR blocked) —</option>
                  <option value="(b)(1)">(b)(1) – Government agency</option>
                  <option value="(b)(2)">(b)(2) – Motor vehicle / driver safety and theft</option>
                  <option value="(b)(3)">(b)(3) – Legitimate business need</option>
                  <option value="(b)(6)">(b)(6) – Insurance company or agent</option>
                  <option value="(b)(8)">(b)(8) – Licensed private investigation</option>
                  <option value="(b)(9)">(b)(9) – Employer verification of CDL information</option>
                </select>
                <p className="text-xs text-[#64748b] mt-2">
                  DPPA §2721(b) permissible use for this client&apos;s MVR lookups. It is recorded on every lookup and sent to
                  states that ask for one. Clients without a use have no MVR access.
                </p>
              </div>
              <div>
                <Label>NMVTIS Customer Category</Label>
                <select
                  value={form.nmvtisCustomerCategory}
                  onChange={(e) => setF('nmvtisCustomerCategory', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  <option value="">— Not set —</option>
                  <option value="Dealer">Dealer</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Finance/Accounting">Finance/Accounting</option>
                  <option value="Auto Auction">Auto Auction</option>
                  <option value="Towing">Towing</option>
                  <option value="Salvage Yard">Salvage Yard</option>
                  <option value="Recycler">Recycler</option>
                  <option value="Salvage Pools">Salvage Pools</option>
                  <option value="Other">Other</option>
                </select>
                <p className="text-xs text-[#64748b] mt-2">
                  AAMVA customer category for NMVTIS quarterly reporting. Uncategorized clients are grouped separately in generated reports.
                </p>
              </div>
            </div>
          </Section>

          {/* Only a SystemAdmin may upload, replace or remove a signed agreement — a
              CanManageClients user administers the organization but does not handle its
              contracts. The API enforces this; the guard here just avoids showing controls
              that would be refused. */}
          {isSystemAdmin && (
            <Section title="Documents">
              <ClientDocuments clientId={data.id} canManage />
            </Section>
          )}

          <Section title="Invoice Recipients" defaultOpen={false}>
            <p className="text-xs text-[#64748b] mb-4">
              Email addresses that receive invoices for this organization. If none are set, the client's contact email is used automatically.
            </p>
            <InvoiceRecipientsList clientId={data.id} />
          </Section>

          <ClientServicePricingSection clientId={data.id} />

          <ClientPricingTiersSection
            clientId={data.id}
            tieredPricingEnabled={data.tieredPricingEnabled}
            onTieredPricingChange={(enabled) =>
              setData((prev) => (prev ? { ...prev, tieredPricingEnabled: enabled } : prev))
            }
          />

          <ClientStatePricingSection clientId={data.id} defaultOpen={false} />

          <ClientUsersKeysSection clientId={data.id} />

          {isSystemAdmin && (
            <div className="bg-white rounded-2xl border border-red-200 p-6 shadow-sm">
              <h3 className="text-sm font-bold text-red-700 flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4" /> Danger Zone</h3>
              <p className="text-sm text-[#64748b] mb-4">
                Permanently delete this organization — its users, API keys, invitations, discounts and reports.
                API request logs and login history are kept for audit. This cannot be undone.
              </p>
              <button
                onClick={() => { setDeleteConfirm(''); setShowDelete(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 font-medium transition-all"
              >
                <Trash2 className="w-4 h-4" /> Delete organization
              </button>
            </div>
          )}

          <div className="sticky bottom-4 bg-white rounded-2xl border border-black/[0.08] p-4 shadow-lg flex items-center justify-between gap-3">
            <div className="text-xs text-[#64748b]">{dirty ? 'Unsaved changes' : 'No changes'}</div>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all shadow-lg shadow-[#c41e3a]/20"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
          </div>
        </div>
      </main>

      {isSystemAdmin && showDelete && data && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-red-700 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Delete {data.name}?</h2>
              <button onClick={() => setShowDelete(false)} className="p-1.5 rounded-lg hover:bg-black/5 text-[#475569]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-4">
              This permanently deletes the organization and all of its users, API keys, invitations, discounts and reports.
              API request logs and login history are kept. <strong>This cannot be undone.</strong>
            </p>
            <label className="block text-xs font-semibold text-[#475569] mb-1">Type <span className="font-mono text-[#0f172a]">{data.name}</span> to confirm</label>
            <input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.1] text-sm mb-4 focus:outline-none focus:border-red-400"
              placeholder={data.name}
            />
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setShowDelete(false)} disabled={deleting} className="px-4 py-2 rounded-lg border border-black/[0.1] text-[#475569] text-sm font-medium hover:bg-black/[0.03]">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting || deleteConfirm !== data.name}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4" /> Delete permanently</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ClientEdit;
